/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { InterviewDetailResponse } from 'src/openapi';
import type { InterviewState } from '../types/interview';
import {
  cancelInterview,
  fetchInterviewTodayCount,
  fetchMyInterviewTodayCount,
  fetchMyInterviews,
  fetchOrgInterviews,
  fetchAdminRecordings,
  fetchAdminTranscript,
  fetchRecording,
  joinInterview,
  rescheduleInterview,
  respondInterview,
  scheduleInterview,
  unlockRecording,
} from '../actions/interview';

const initialState: InterviewState = {
  interviews: [],
  todayCount: 0,
  myTodayCount: 0,
  isFetching: false,
  page: 1,
  totalPages: 1,
  isScheduling: false,
  isScheduleDialogOpen: false,
  activeInterview: null,
  recordingByInterview: {},
  unlockingRecordingIds: [],
  adminRecordings: [],
  adminRecordingsPage: 1,
  adminRecordingsTotalPages: 1,
  adminRecordingsTotal: 0,
  adminRecordingsPageSize: 20,
  isFetchingAdminRecordings: false,
  transcriptByInterview: {},
  fetchingTranscriptIds: [],
  joinInfo: null,
  isJoining: false,
};

const upsert = (
  list: InterviewDetailResponse[],
  updated: InterviewDetailResponse,
) => list.map((i) => (i.id === updated.id ? { ...i, ...updated } : i));

const ACTIVE_STATUSES = ['Scheduled', 'Accepted', 'Rescheduled'];
// Count of all upcoming (active, not-yet-passed) interviews — drives the badge.
const countUpcomingActive = (list: InterviewDetailResponse[]) => {
  const now = Date.now();
  return list.filter(
    (iv) =>
      ACTIVE_STATUSES.includes(iv.status ?? '') &&
      !!iv.scheduled_start_at &&
      new Date(iv.scheduled_start_at).getTime() >= now,
  ).length;
};

const interviewSlice = createSlice({
  name: 'interview',
  initialState,
  reducers: {
    setScheduleDialogOpen: (
      state,
      action: PayloadAction<{
        open: boolean;
        candidate?: InterviewDetailResponse | null;
      }>,
    ) => {
      state.isScheduleDialogOpen = action.payload.open;
      state.activeInterview = action.payload.candidate ?? null;
    },
    clearJoinInfo: (state) => {
      state.joinInfo = null;
    },
  },
  extraReducers: (builder) => {
    const onListPending = (state: InterviewState) => {
      state.isFetching = true;
    };
    const onListFulfilled = (state: InterviewState, action: any) => {
      state.isFetching = false;
      state.interviews = action.payload.data ?? [];
      state.page = action.payload.current_page ?? 1;
      state.totalPages = action.payload.total_pages ?? 1;
    };
    const onListRejected = (state: InterviewState) => {
      state.isFetching = false;
    };

    builder
      .addCase(fetchOrgInterviews.pending, onListPending)
      .addCase(fetchOrgInterviews.fulfilled, (state, action) => {
        onListFulfilled(state, action);
        state.todayCount = countUpcomingActive(action.payload.data ?? []);
      })
      .addCase(fetchOrgInterviews.rejected, onListRejected)
      .addCase(fetchMyInterviews.pending, onListPending)
      .addCase(fetchMyInterviews.fulfilled, (state, action) => {
        onListFulfilled(state, action);
        state.myTodayCount = countUpcomingActive(action.payload.data ?? []);
      })
      .addCase(fetchMyInterviews.rejected, onListRejected)

      .addCase(fetchInterviewTodayCount.fulfilled, (state, action) => {
        state.todayCount = action.payload ?? 0;
      })
      .addCase(fetchMyInterviewTodayCount.fulfilled, (state, action) => {
        state.myTodayCount = action.payload ?? 0;
      })

      .addCase(scheduleInterview.pending, (state) => {
        state.isScheduling = true;
      })
      .addCase(scheduleInterview.fulfilled, (state) => {
        state.isScheduling = false;
        state.isScheduleDialogOpen = false;
      })
      .addCase(scheduleInterview.rejected, (state) => {
        state.isScheduling = false;
      })

      .addCase(rescheduleInterview.fulfilled, (state, action) => {
        state.interviews = upsert(
          state.interviews,
          action.payload as InterviewDetailResponse,
        );
      })
      .addCase(cancelInterview.fulfilled, (state, action) => {
        state.interviews = upsert(
          state.interviews,
          action.payload as InterviewDetailResponse,
        );
      })
      .addCase(respondInterview.fulfilled, (state, action) => {
        state.interviews = upsert(
          state.interviews,
          action.payload as InterviewDetailResponse,
        );
      })

      .addCase(joinInterview.pending, (state) => {
        state.isJoining = true;
        state.joinInfo = null;
      })
      .addCase(joinInterview.fulfilled, (state, action) => {
        state.isJoining = false;
        state.joinInfo = action.payload;
      })
      .addCase(joinInterview.rejected, (state) => {
        state.isJoining = false;
      })

      .addCase(fetchRecording.fulfilled, (state, action) => {
        const id = action.payload.interview_id;
        if (id) state.recordingByInterview[id] = action.payload;
      })
      .addCase(unlockRecording.pending, (state, action) => {
        state.unlockingRecordingIds.push(action.meta.arg.interviewId);
      })
      .addCase(unlockRecording.fulfilled, (state, action) => {
        const id = action.meta.arg.interviewId;
        state.unlockingRecordingIds = state.unlockingRecordingIds.filter(
          (x) => x !== id,
        );
        state.recordingByInterview[id] = action.payload;
      })
      .addCase(unlockRecording.rejected, (state, action) => {
        const id = action.meta.arg.interviewId;
        state.unlockingRecordingIds = state.unlockingRecordingIds.filter(
          (x) => x !== id,
        );
      })

      .addCase(fetchAdminRecordings.pending, (state) => {
        state.isFetchingAdminRecordings = true;
      })
      .addCase(fetchAdminRecordings.fulfilled, (state, action) => {
        state.isFetchingAdminRecordings = false;
        state.adminRecordings = action.payload.data ?? [];
        state.adminRecordingsPage = action.payload.current_page ?? 1;
        state.adminRecordingsTotalPages = action.payload.total_pages ?? 1;
        state.adminRecordingsTotal = action.payload.total ?? 0;
        state.adminRecordingsPageSize = action.payload.page_size ?? 20;
      })
      .addCase(fetchAdminRecordings.rejected, (state) => {
        state.isFetchingAdminRecordings = false;
      })

      .addCase(fetchAdminTranscript.pending, (state, action) => {
        state.fetchingTranscriptIds.push(action.meta.arg.interviewId);
      })
      .addCase(fetchAdminTranscript.fulfilled, (state, action) => {
        const id = action.meta.arg.interviewId;
        state.fetchingTranscriptIds = state.fetchingTranscriptIds.filter(
          (x) => x !== id,
        );
        state.transcriptByInterview[id] = action.payload;
      })
      .addCase(fetchAdminTranscript.rejected, (state, action) => {
        const id = action.meta.arg.interviewId;
        state.fetchingTranscriptIds = state.fetchingTranscriptIds.filter(
          (x) => x !== id,
        );
      });
  },
});

export const { setScheduleDialogOpen, clearJoinInfo } = interviewSlice.actions;
export default interviewSlice.reducer;
